import React, { useMemo, useState, useRef, useContext, useCallback, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
    FormTitle,
    LoginContainer,
    LoginContent,
    RegisterContainer,
    Container,
    FormText
} from './styled'

import Button from "components/Form/Button";
import Input from "components/Form/Input";

import ContainerUnauthenticated from "containers/Unauthenticated";
import { DoRegister, VerifyOtp, ResendOtp, FinalizePostVerifyEmails } from "services/authentication";
import { exposeStrapiError } from "utils";
import OfficeTabs from "components/OfficeTabs";
import FormCore from "../../../components/Form/Core";
import TermCheck from "components/TermCheck";
import { ButtonContainer } from "ui/styled";
import FormSuccess from "components/FormSuccess";
import { industryOptions, optionsCountries, optionsGermanyZipcodes, optionsSalutation, optionsUsaStates } from "utils/options";
import { CoreContext } from "context/CoreContext";
import useI18n from "hooks/useI18n";
import { API_ENDPOINT } from "services/api";
import { trackEvent } from "services/analytics";
import { ReadContactPrefill } from "services/salesforce";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

export default function Register() {

    const history = useHistory();
    const location = useLocation();
    const navigate = useCallback(to => history.push(`/${to}`), [history]);

    const { setModal } = useContext(CoreContext)

    const { t } = useI18n()

    const [loading, setLoading] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    const [register, setRegister] = useState({

    })


    const [termsAccepted, setTermsAccepted] = useState(false)
    const [subscribeNews, setSubscribeNews] = useState(false)
    const [success, setSuccess] = useState(null)

    const [otpEmail, setOtpEmail] = useState(null);
    const [otpCode, setOtpCode] = useState('');
    const [otpTtl, setOtpTtl] = useState(0);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [verifying, setVerifying] = useState(false);

    const formRef = useRef()
    const recaptchaRef = useRef(null);
    const otpRef = useRef(null);
    const submissionRef = useRef({ register: false, verify: false });
    const [recaptchaToken, setRecaptchaToken] = useState(null);
    const [salesforceContactId, setSalesforceContactId] = useState(null);
    const [salesforceAccountId, setSalesforceAccountId] = useState(null);

    useEffect(() => {
        trackEvent('registration_start', {
            method: 'email',
            screen_name: 'register',
        });
    }, []);

    useEffect(() => {
        let cancelled = false;
        const params = new URLSearchParams(location.search);
        const contactId = String(params.get('contactId') || params.get('token') || '').trim();
        if (!contactId) return undefined;

        const loadContact = async () => {
            const result = await ReadContactPrefill(contactId);
            if (cancelled) return;
            if (result?.error) {
                toast.error(result?.error?.message || "Invalid Salesforce contact link.");
                return;
            }
            setSalesforceContactId(result.salesforceContactId || result.contactId || contactId);
            setSalesforceAccountId(result.salesforceAccountId || null);
            setRegister((current) => ({
                ...current,
                firstName: result.firstName || current.firstName,
                lastName: result.lastName || current.lastName,
                email: result.email || current.email,
                companyName: result.companyName || current.companyName,
            }));
        };

        loadContact();
        return () => { cancelled = true; };
    }, [location.search]);

    const updateRegister = useCallback(() => {
        const form = formRef?.current?.getForm(true)
        setRegister({ ...register, ...form })
    }, [register])

    const completeLogin = useCallback(async (payload) => {
        trackEvent('registration_complete', {
            method: 'email',
            screen_name: 'register',
        });
        navigate('login')
    }, [navigate])

    const onCaptchaChange = useCallback((token) => {
        setRecaptchaToken(token);
    }, []);

    useEffect(() => {
        if (!otpTtl) return;
        const id = setInterval(() => {
            setOtpTtl((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(id);
    }, [otpTtl]);

    useEffect(() => {
        if (!resendCooldown) return;
        const id = setInterval(() => {
            setResendCooldown((s) => (s > 0 ? s - 1 : 0));
        }, 1000);
        return () => clearInterval(id);
    }, [resendCooldown]);

    const action = useCallback(async (saved) => {

        if (submissionRef.current.register) return;

        if (!recaptchaToken) {
            toast.error(t("please_confirm_you_are_not_a_robot") || "Please confirm you are not a robot.");
            return;
        }

            const payload = {
                ...saved,
                ...(salesforceContactId ? { salesforceContactId } : {}),
                ...(salesforceAccountId ? { salesforceAccountId } : {}),
                registrationSource: 'universe',
            };

        submissionRef.current.register = true;
        setLoading(true)
        try {
            const result = await DoRegister({
                confirmed: true,
                blocked: false,
                role: 1,
                ...payload,
                password: payload.password,
                username: payload?.email?.replace(/ /g, ''),
                email: payload?.email?.replace(/ /g, ''),
                recaptchaToken,
            })

            if (result && !exposeStrapiError(result)) {
                if (result?.requiresOtp) {
                    setOtpEmail(result?.email || payload?.email);
                    setOtpTtl(Number(result?.ttlSeconds || 600));
                    setResendCooldown(60);
                    setActiveStep(1);
                    recaptchaRef?.current?.reset?.();
                    setRecaptchaToken(null);
                } else {
                    setSuccess({
                        title: t("success"),
                        text: t("account_successfully_created"),
                        label: t("close"),
                        action: async () => await completeLogin(payload)
                    })
                }
            } else {
                recaptchaRef?.current?.reset?.();
                setRecaptchaToken(null);
            }
        } finally {
            submissionRef.current.register = false;
            setLoading(false)
        }
    }, [t, completeLogin, recaptchaToken, salesforceAccountId, salesforceContactId])

    const verifyOtp = useCallback(async () => {
        if (submissionRef.current.verify) return false;
        if (!otpEmail || !otpCode || otpCode.length < 6) {
            toast.error(t("enter_the_code_sent_to_your_email") || "Enter the code sent to your email.");
            return false;
        }
        submissionRef.current.verify = true;
        setVerifying(true);
        try {
            const data = await VerifyOtp({ email: otpEmail, code: otpCode });
            if (data?.error) {
                toast.error(data?.error?.message || data?.message || "Invalid or expired code.");
                return false;
            }
            try {
                await FinalizePostVerifyEmails({ email: otpEmail });
            } catch (e) {
                console.warn('finalize-post-verify-emails failed', e);
            }

            setSuccess({
                title: t("success"),
                text: t("account_successfully_created"),
                label: t("close"),
                action: async () => await completeLogin()
            })
            return true;
        } catch (e) {
            toast.error("Network error verifying code.");
            return false;
        } finally {
            submissionRef.current.verify = false;
            setVerifying(false);
        }
    }, [otpEmail, otpCode, t, completeLogin]);

    const resendOtp = useCallback(async () => {
        if (resendCooldown > 0) return;
        if (!otpEmail) {
            toast.error("Missing email.");
            return;
        }
        try {
            const data = await ResendOtp({ email: otpEmail });
            if (data?.error) {
                toast.error(data?.error?.message || data?.message || "Unable to resend code.");
                return;
            }
            setOtpTtl(Number(data?.ttlSeconds || 600));
            setResendCooldown(60);
            toast.success(t("a_new_code_has_been_sent") || "A new code has been sent.");
        } catch (e) {
            toast.error("Network error resending code.");
        }
    }, [otpEmail, resendCooldown, t]);

    const options = useMemo(() => [
        { title: t("login"), page: `login` },
        { title: t("register"), page: `register` },
    ], [t])

    const formItems = useMemo(() => [
        {
            ref: 'salutation',
            placeholder: t("salutation"),
            options: optionsSalutation,
            required: true
        },
        {
            ref: 'firstName',
            placeholder: t("first_name"),
            required: true
        },
        {
            ref: 'lastName',
            placeholder: t("last_name"),
            required: true
        },
        {
            ref: 'companyName',
            placeholder: t("company_name"),
            required: true,
            half: true
        },
        {
            ref: 'jobTitle',
            placeholder: t("job_title"),
            required: true,
            half: true
        },
        {
            ref: 'industry',
            placeholder: t("industry"),
            options: industryOptions,
            required: true,
            half: true
        },
        {
            ref: 'country',
            placeholder: t("country"),
            options: optionsCountries,
            required: true,
            half: true,
            onBlur: () => updateRegister()
        },
        ...(
            register?.country === "United States" ? [
                {
                    ref: 'state',
                    placeholder: t("state"),
                    options: optionsUsaStates,
                    full: true,
                    required: true,
                },
            ] : register?.country === "germany" ? [
                {
                    ref: 'zipcode',
                    placeholder: t("zipcode"),
                    options: optionsGermanyZipcodes,
                    full: true,
                    required: true
                }
            ] : [
                // {
                //     ref: 'state',
                //     placeholder: t("state"),
                //     // options: [],
                //     full: true,
                //     required: true,
                // },

            ]
        ),
        {
            custom: <FormText>{t("registration_professional_email_hint")}</FormText>,
            full: true,
        },
        {
            ref: 'email',
            id: 'email',
            placeholder: t("email"),
            half: true,
            required: true,
            onBlur: () => updateRegister()
        },
        {
            ref: 'phone',
            placeholder: t("phone_number"),
            half: true,
            type: 'phone',
        },
        { ref: 'password', label: t('password'), placeholder: t('password'), type: 'password', required: true, half: true },
        { ref: 'passwordConfirm', label: t('password_confirmation'), placeholder: t('password_confirmation'), type: 'password', required: true, half: true },
        { passwordValidation: true, full: true },
    ], [t, register, updateRegister])

    // const passwordFormItems = useMemo(() => [
    //     { ref: 'password', label: t("password"), placeholder: t("password"), type: 'password', required: true, half: true },
    //     { ref: 'passwordConfirm', label: t("password_confirmation"), type: 'password', placeholder: t("password_confirmation"), required: true, half: true },
    //     { passwordValidation: true, full: true }
    // ], [t])

    const steps = useMemo(() => ([
        { title: t("signup"), text: t("are_you_a_patient_or_a_therapist") },
        { title: t("verify_email"), text: t("enter_the_code_sent_to_your_email") },
    ]), [t])

    const back = () => {
        saveStep(true);
        if (activeStep - 1 > -1 && !success) {
            setActiveStep(activeStep - 1)
            return;
        }
        navigate('login');
    }

    const next = () => {
        if (activeStep === 0) {
            const saved = saveStep(false);
            if (saved) {
                if (activeStep + 1 < steps.length) {
                    // Em vez de avançar diretamente, o action controla se vamos para OTP
                    action(saved);
                    return;
                }
            }
            return;
        }
        if (activeStep === 1) {
            verifyOtp();
        }
    }

    const saveStep = useCallback((skip) => {
        if (activeStep === 0) {
            const form = formRef?.current?.getForm(skip)
            if (!!form) {
                if (!termsAccepted && !skip) {
                    toast.error(t('accept_terms'))
                    return false;
                }
                if (!skip && form.password !== form.passwordConfirm) {
                    toast.error(t('password_and_password_confirmation_must_match'));
                    return false;
                }
                const nextRegister = { ...register, ...form }
                setRegister(nextRegister);
                return nextRegister;
            }
            return false;
        }
        const formPassword = { password: register.password, passwordConfirm: register.passwordConfirm }

        if (!!formPassword) {
            if (formPassword?.password !== formPassword?.passwordConfirm) {
                toast.error(t('password_and_password_confirmation_must_match'))
                return false;
            }
            return { ...register, ...formPassword };
        }
    }, [t, activeStep, register, termsAccepted])

    const handleBeforeUnload = useCallback((event) => {
        event.preventDefault();
        event.returnValue = '';
        const email = register?.email
        if (!success && email) {
            const url = `${API_ENDPOINT}/tmp-email?email=${email}`;
            fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' }, keepalive: true });
            console.log("done")
        }
    }, [success, register])

    useEffect(() => {
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => { window.removeEventListener('beforeunload', handleBeforeUnload); };
    }, [handleBeforeUnload]);

    return (
        <>
            {!success ? null : <FormSuccess success={success} />}
            {
                success ? null :

                    <ContainerUnauthenticated>
                        <LoginContent>
                            <OfficeTabs options={options} />
                            <LoginContainer>
                                {
                                    !(activeStep === 0) ? null :
                                        <RegisterContainer>
                                            <FormTitle>{t("create_your_account")}</FormTitle>
                                            <FormCore ref={formRef} formItems={formItems} register={register} />

                                            <Container>
                                                <TermCheck title={t("subscribe_to_newsletter")} subtext={t("subscribe_to_newsletter_information")} value={subscribeNews} onChange={setSubscribeNews} />
                                                <TermCheck title={t("i_accept_the")} text={register?.country === "united_states" ? t("i_accept_the_united") : t("privacy_policy")} value={termsAccepted} onChange={setTermsAccepted} link={() => {
                                                    if (register?.country === 'United States' || register?.country === 'united_states') {
                                                        window.open('/policy.pdf', '_blank');
                                                        return;
                                                    }
                                                    setModal({ type: 'term' })
                                                }} />
                                                <FormText>{t("register.undertems")}</FormText>
                                            </Container>
                                            <div style={{ margin: '12px 0' }}>
                                                <ReCAPTCHA
                                                    ref={recaptchaRef}
                                                    sitekey={RECAPTCHA_SITE_KEY}
                                                    onChange={onCaptchaChange}
                                                />
                                            </div>
                                            <ButtonContainer space>
                                                <Button color="blue" outline onClick={back}>{t("back")}</Button>
                                                <Button color="blue" loading={loading} disabled={loading} onClick={next}>{t("next")}</Button>
                                            </ButtonContainer>
                                        </RegisterContainer>
                                }
                                {
                                    !(activeStep === 1) ? null :
                                        <RegisterContainer>
                                            <FormTitle>{t("verify_email")}</FormTitle>
                                            <FormText>
                                                {t("we_sent_a_code_to_email") || "We sent a verification code to"}: <strong>{otpEmail}</strong>
                                            </FormText>
                                            <div style={{ margin: '16px 0' }}>
                                                <Input
                                                    ref={otpRef}
                                                    id="otp"
                                                    placeholder={t("enter_verification_code") || "Enter verification code"}
                                                    value={otpCode}
                                                    onChange={e => setOtpCode(e?.target?.value?.replace(/\D/g, '').slice(0, 6))}
                                                    maxLength={6}
                                                />
                                                <FormText style={{ marginTop: 8 }}>
                                                    {t("code_expires_in") || "Code expires in"}: {Math.floor(otpTtl / 60)}:{String(otpTtl % 60).padStart(2, '0')}
                                                </FormText>
                                            </div>
                                            <ButtonContainer space>
                                                <Button color="blue" loading={verifying} disabled={verifying} onClick={verifyOtp}>{t("verify")}</Button>
                                            </ButtonContainer>
                                            <div style={{ marginTop: 12 }}>
                                                <Button
                                                    color="blue"
                                                    outline
                                                    disabled={resendCooldown > 0}
                                                    onClick={resendOtp}
                                                >
                                                    {resendCooldown > 0
                                                        ? `${t("resend_code_in") || "Resend code in"} ${resendCooldown}s`
                                                        : t("resend_code") || "Resend code"}
                                                </Button>
                                            </div>
                                        </RegisterContainer>
                                }
                                {/* {
                                    !(activeStep === 1) ? null : <>
                                        <RegisterContainer>
                                            <FormTitle>{t("create_a_password")}</FormTitle>
                                            <FormText>{t("enter_a_password_for_your_account")}</FormText>
                                            <FormCore ref={formPasswordRef} formItems={passwordFormItems} register={passwordRegister} />
                                            <ButtonContainer space>
                                                <Button color="blue" outline onClick={back}>{t("back")}</Button>
                                                <Button color="blue" loading={loading} onClick={next}>{t("next")}</Button>
                                            </ButtonContainer>
                                        </RegisterContainer>
                                    </>
                                } */}
                            </LoginContainer>
                        </LoginContent>
                    </ContainerUnauthenticated>
            }
        </>
    );
}
